import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  CircularProgress,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import MessageIcon from '@mui/icons-material/Message';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import QuizIcon from '@mui/icons-material/Quiz';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PhotoIcon from '@mui/icons-material/Photo';
import VideocamIcon from '@mui/icons-material/Videocam';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWhitelabel } from '../context/WhitelabelContext';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import BottomTabBar from './BottomTabBar';

/**
 * Returns true when running inside the React Native WebView shell.
 * BRIDGE_JS sets window.__RN_WEBVIEW = true as its very first statement
 * (before the NativeBridge guard) via injectedJavaScriptBeforeContentLoaded,
 * so this value is always set before React's first render.
 */
const detectWebView = () => {
  try {
    // window.__RN_WEBVIEW is set by BRIDGE_JS as its very first statement,
    // before the NativeBridge guard, so it is always present when React reads it.
    if (window.__RN_WEBVIEW) return true;
    if (window.ReactNativeWebView) return true;
    if (window.NativeBridge) return true;
    return false;
  } catch {
    return false;
  }
};

const Layout = ({ children }) => {
  const [isWebView, setIsWebView] = React.useState(() => detectWebView());
  const [drawerOpen, setDrawerOpen] = React.useState(true);

  // Re-check after mount in case ReactNativeWebView was set just before render
  React.useEffect(() => {
    if (!isWebView && detectWebView()) setIsWebView(true);
  }, []);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { config: whitelabelConfig } = useWhitelabel();

  useEffect(() => {
    loadStudentInfo();
    loadSchoolInfo();
    loadNotificationCount();
    
    // Poll for notification count every 30 seconds
    const interval = setInterval(() => {
      loadNotificationCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadStudentInfo = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/master/student/get-student-profile', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      }); 
      setStudentInfo(response.data);
      // Send full profile to native shell for the sidebar
      window.NativeBridge?.setUserInfo(response.data);
    } catch (error) {
      console.error('Layout loadStudentInfo error:', error);
      handleCatch(error);
    } finally {
      setLoadingStudent(false);
    }
  };

  const loadSchoolInfo = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/token/get-school-info', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      setSchoolInfo(response.data);
    } catch (error) {
      console.error('Layout loadSchoolInfo error:', error);
      handleCatch(error);
    }
  };

  const loadNotificationCount = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await api.get('api/school-common/notification/get-notification?PageNumber=1&PageSize=100', {
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      const notifications = response.data.data || [];
      const unreadCount = notifications.filter(n => !n.isRead).length;
      setUnreadNotificationCount(unreadCount);
    } catch (error) {
      console.error('Layout loadNotificationCount error:', error);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    { title: 'Home', icon: <HomeIcon />, path: '/home' },
    { title: 'Attendance', icon: <EventIcon />, path: '/attendance' },
    { title: 'Calendar', icon: <EventIcon />, path: '/holiday-calendar' },
    { title: 'Assignments', icon: <AssignmentIcon />, path: '/assignments' },
    { title: 'Notices', icon: <AnnouncementIcon />, path: '/notices' },
    { title: 'Messages', icon: <MessageIcon />, path: '/messages' },
    { title: 'Online Classes', icon: <VideoCallIcon />, path: '/online-classes' },
    { title: 'Online Exams', icon: <QuizIcon />, path: '/online-exams' },
    { title: 'Bills', icon: <ReceiptIcon />, path: '/bills' },
    { title: 'Receipts', icon: <ReceiptIcon />, path: '/receipts' },
    { title: 'Photo Gallery', icon: <PhotoIcon />, path: '/gallery' },
    { title: 'Video Gallery', icon: <VideocamIcon />, path: '/videos' },
    { title: 'Circulars', icon: <AnnouncementIcon />, path: '/circulars' },
    { title: 'My Teachers', icon: <PeopleIcon />, path: '/teachers' },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    handleMenuClose();
    // Notify the native shell to clear the stored session
    window.NativeBridge?.clearSession();
    signOut();
    navigate('/login');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleChangePasswordClick = () => {
    handleMenuClose();
    navigate('/change-password');
  };

  // ── WebView mode: native shell provides header + drawer ──────────────
  // Return children in a simple full-screen scrollable box.
  // The NativeDrawer and NavigationHeader in App.js handle chrome.
  if (isWebView) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          overflow: 'auto',
          backgroundColor: '#F5F7FA',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </Box>
    );
  }

  // ── Browser / desktop mode: full sidebar + AppBar layout ─────────────
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Persistent Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerOpen ? 240 : 72,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerOpen ? 240 : 72,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: 'width 0.3s',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <Toolbar />
        
        {/* Student Info Section */}
        {drawerOpen ? (
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#f8f9fa',
          }}>
            {loadingStudent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : studentInfo ? (
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={studentInfo.photoUrl}
                  sx={{
                    width: 80,
                    height: 80,
                    margin: '0 auto 12px',
                    border: '3px solid ' + colors.BLUE,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  {studentInfo.studentName?.charAt(0)?.toUpperCase() || 'S'}
                </Avatar>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 0.5 }}>
                  {studentInfo.studentName}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.BLUE, fontWeight: 500, display: 'block', mb: 0.3 }}>
                  Class: {studentInfo.class} - {studentInfo.section}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Roll: {studentInfo.rollNo}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.3 }}>
                  Reg: {studentInfo.regNo}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ 
            p: 1, 
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            justifyContent: 'center',
          }}>
            {!loadingStudent && studentInfo && (
              <Avatar
                src={studentInfo.photoUrl}
                sx={{
                  width: 48,
                  height: 48,
                  border: '2px solid ' + colors.BLUE,
                }}
              >
                {studentInfo.studentName?.charAt(0)?.toUpperCase() || 'S'}
              </Avatar>
            )}
          </Box>
        )}
        
        <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <List sx={{ flex: 1 }}>
            {menuItems.map((item) => (
              <ListItem 
                button 
                key={item.title} 
                onClick={() => handleMenuClick(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 102, 204, 0.08)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 3 : 'auto',
                    justifyContent: 'center',
                    color: colors.BLUE,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {drawerOpen && <ListItemText primary={item.title} />}
              </ListItem>
            ))}
          </List>
          
          {/* Company Name at Bottom */}
          {whitelabelConfig?.organisationName && (
            <Box
              sx={{
                p: drawerOpen ? 2 : 1,
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: '#f8f9fa',
                textAlign: 'center',
              }}
            >
              {drawerOpen ? (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Developed by
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.BLUE }}>
                    {whitelabelConfig.organisationName}
                  </Typography>
                </>
              ) : (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    color: colors.BLUE,
                    fontWeight: 600,
                  }}
                >
                  {whitelabelConfig.organisationName.substring(0, 3)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, overflow: 'auto', height: '100vh' }}>
        <AppBar position="fixed" sx={{ backgroundColor: colors.BLUE, zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(!drawerOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {schoolInfo?.schoolName || 'Student Mobile App'}
            </Typography>
            
            {/* Notification Bell */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/notifications')}
              sx={{ mr: 2 }}
            >
              <Badge badgeContent={unreadNotificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            
            {/* Organization Logo */}
            {whitelabelConfig?.logoUrl && (
              <Box
                component="img"
                src={whitelabelConfig.logoUrl}
                alt="Organization Logo"
                sx={{
                  height: 40,
                  maxWidth: 150,
                  objectFit: 'contain',
                  mr: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  padding: 0.5,
                }}
              />
            )}
            
            {/* User Menu */}
            <IconButton
              onClick={handleMenuOpen}
              sx={{ 
                p: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: colors.ORANGE,
                  width: 36,
                  height: 36,
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'S'}
              </Avatar>
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.name || 'Student'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {user?.email || ''}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleProfileClick} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>My Profile</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleChangePasswordClick} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box component="main" sx={{ p: 0, backgroundColor: '#f5f5f5', mt: 8 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
